import { QueryBuilderTextField } from '@modules/queryBuilder/components/QueryBuilder/QueryBuilderTextField';
import { QueryBuilderActions } from '@modules/queryBuilder/components/QueryBuilderActions';
import { QueryBuilderFooter } from '@modules/queryBuilder/components/QueryBuilderFooter';
import { QueryFilter } from '@modules/queryBuilder/components/QueryFilter';
import { QueryFilterGroup } from '@modules/queryBuilder/components/QueryFilterGroup';
import { QueryFilterSelect } from '@modules/queryBuilder/components/QueryFilterSelect';
import {
  useClearQueryBuilderEqlCache,
  useQueryChangeNotifier,
  useResetQuery,
  useSaveFilterIDToValueMapInStore,
} from '@modules/queryBuilder/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { QueryOnChangeProps } from '@modules/queryBuilder/types';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import {
  ButtonProps,
  Grid,
  Skeleton,
  Stack,
  SxProps,
  TextFieldProps,
  Theme,
} from '@mui/material';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface QueryBuilderProps {
  whereClause?: WhereClause;
  onChange?: (params: QueryOnChangeProps) => void;
  onExecute?: (params: QueryOnChangeProps) => void;
  forceEnableExecuteButton?: boolean;
  shouldSkipRender?: boolean;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  shouldSkipWarningMessage?: boolean;
  actionsProps?: {
    shouldRenderExecuteButton?: boolean;
  };
  footerProps?: {
    shouldRenderCreateNewFilter?: boolean;
  };
  buttonColor?: ButtonProps['color'];
  containerSxProps?: SxProps<Theme>;
  readOnly?: boolean;
  secondaryActionNode?: React.ReactNode;
  textFieldInputProps?: TextFieldProps['InputProps'];
}

export interface QueryBuilderActionsHandle {
  resetQuery: (whereClause?: WhereClause) => void;
}

export const QueryBuilder = forwardRef<
  QueryBuilderActionsHandle,
  QueryBuilderProps
>(
  (
    {
      onChange = () => {},
      onExecute,
      whereClause: whereClauseProp = EmptyWhereClause,
      forceEnableExecuteButton = false,
      shouldSkipRender = false,
      autoFocus = false,
      openOnFocus = true,
      actionsProps = {},
      footerProps = {},
      shouldSkipWarningMessage = false,
      buttonColor = 'primary',
      containerSxProps,
      readOnly = false,
      secondaryActionNode,
      textFieldInputProps,
    },
    forwardedRef,
  ) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const nodes = useQueryBuilderStore(({ query }) => query.nodes);
    const rootID = useQueryBuilderStore(({ query }) => query.rootID);
    const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
    const isActive = useQueryBuilderStore(({ metadata }) => metadata.isActive);
    const isLoading = useQueryBuilderStore(
      ({ metadata }) => metadata.isLoading,
    );
    const isEmptyQuery = useQueryBuilderStore(
      ({ metadata }) => metadata.isEmpty,
    );
    const whereClause = useQueryBuilderStore(
      ({ metadata }) => metadata.whereClause,
    );
    const shouldAutoExecuteOnce = useQueryBuilderStore(
      ({ metadata }) => metadata.shouldAutoExecuteOnce,
    );
    const shouldShowWarning = useQueryBuilderStore(
      ({ metadata }) =>
        !metadata.isActive &&
        metadata.hasUnsavedChanges &&
        !metadata.hasIncompleteFilters &&
        !metadata.error &&
        !shouldSkipWarningMessage,
    );

    const { isLoading: isFetchingFilterValues } =
      useSaveFilterIDToValueMapInStore({
        whereClause: whereClauseProp,
      });

    useQueryChangeNotifier(onChange);
    const resetQuery = useResetQuery(whereClauseProp);

    useImperativeHandle(forwardedRef, () => ({
      resetQuery,
    }));

    useClearQueryBuilderEqlCache();

    // Allows consumers to auto-execute the query. Ex: QuickFilters
    useUpdateEffect(() => {
      if (shouldAutoExecuteOnce) {
        onExecute?.({
          whereClause,
          isQuickFilter: true,
        });

        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: {
            shouldAutoExecuteOnce: false,
            hasUnsavedChanges: false,
            isActive: false,
          },
        });
      }
    }, [whereClause]);

    const renderFilters = () => {
      const rootNode = nodes[rootID];

      if (isEmptyQuery) {
        return (
          <QueryFilterSelect
            parentID={rootID}
            buttonColor={buttonColor}
            readOnly={readOnly}
          />
        );
      }

      if (rootNode.type === 'group') {
        return rootNode.children.map((id) => {
          const node = nodes[id];

          if (node.type === 'group') {
            return (
              <QueryFilterGroup
                key={node.id}
                filterGroup={node}
                buttonColor={buttonColor}
                readOnly={readOnly}
              />
            );
          }

          if (node.type === 'filter' && node.field) {
            return (
              <QueryFilter
                key={node.id}
                filter={node}
                buttonColor={buttonColor}
                readOnly={readOnly}
              />
            );
          }

          return (
            <QueryFilterSelect
              key={`${node.id}_filter_select`}
              parentID={node.parent}
              filter={node}
              buttonColor={buttonColor}
              readOnly={readOnly}
            />
          );
        });
      }

      return null;
    };

    const onClickAway = (event: MouseEvent | TouchEvent) => {
      if (anchorRef.current?.contains(event.target as HTMLElement)) {
        return;
      }

      if (isActive) {
        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: { isActive: false },
        });
      }
    };

    const onFocus = () => {
      if (!isActive) {
        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: { isActive: true },
        });
      }
    };

    if (shouldSkipRender) {
      return null;
    }

    if (isFetchingFilterValues || isLoading) {
      return (
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={150} />
          <Skeleton variant="rounded" width={250} />
          <Skeleton variant="rounded" width={150} />
        </Stack>
      );
    }

    const rootNode = nodes[rootID];

    if (!rootNode.children || rootNode.children.length === 0) {
      return (
        <QueryBuilderTextField
          onExecute={onExecute}
          autoFocus={autoFocus}
          forceEnableExecuteButton={forceEnableExecuteButton}
          openOnFocus={openOnFocus}
          shouldRenderExecuteButton={actionsProps.shouldRenderExecuteButton}
          buttonColor={buttonColor}
          inputProps={textFieldInputProps}
        />
      );
    }

    return (
      <ClickAwayListener onClickAway={onClickAway}>
        <Stack ref={anchorRef} onClick={onFocus}>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            border="1px solid"
            borderColor={(theme) =>
              shouldShowWarning
                ? theme.palette.warning.dark
                : theme.palette.divider
            }
            sx={{
              p: 2,
              borderRadius: 2,
              borderBottomLeftRadius: !readOnly ? 0 : undefined,
              borderBottomRightRadius: !readOnly ? 0 : undefined,
              boxShadow: 'none',
              backgroundColor: 'background.paper',
              ...containerSxProps,
            }}
            overflow="auto"
          >
            <Grid item xs>
              <Stack
                direction="column"
                spacing={1}
                justifyContent="center"
                sx={{ minWidth: 'fit-content' }}
              >
                {renderFilters()}
              </Stack>
            </Grid>
            <Grid item xs="auto">
              <QueryBuilderActions
                onExecute={onExecute}
                forceEnableExecuteButton={forceEnableExecuteButton}
                shouldRenderExecuteButton={
                  actionsProps.shouldRenderExecuteButton
                }
                buttonColor={buttonColor}
                secondaryActionNode={secondaryActionNode}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>
          {!readOnly && (
            <Stack
              border="1px solid"
              borderTop={0}
              borderColor={(theme) => theme.palette.divider}
              sx={{
                borderRadius: 2,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              }}
            >
              <QueryBuilderFooter
                shouldRenderCreateNewFilter={
                  footerProps.shouldRenderCreateNewFilter
                }
                shouldSkipWarningMessage={shouldSkipWarningMessage}
                buttonColor={buttonColor}
              />
            </Stack>
          )}
        </Stack>
      </ClickAwayListener>
    );
  },
);
